// Auth API — HAQIQIY backend kontrakti, `xsib-core/selfcare` dan o'qib
// olingan (`internal/LoginController.java`, `internal/MeController.java`):
//
//   POST /api/selfcare/login        {tenantCode, login, password}
//                                    -> {token, expiresAt, subscriberId, login}
//   GET  /api/selfcare/me           -> {login, displayName, kind, state, createdAt,
//                                       dayTimezone}
//   PUT  /api/selfcare/me/password  {currentPassword, newPassword} -> OperationResult
//
// NEGA ZOD: TS tipi kompilyatsiyadan keyin yo'qoladi va serverdan kelgan
// `undefined` maydon jimgina o'tib ketadi. Zod xatoni CHEGARADA ushlaydi.
//
// ⚠️ `tenantCode` bu yerda ICHKI — `shared/config.ts` dan olinadi, UI
// maydoni EMAS (backend `LoginController` javadoc'i: "kabinet frontendi
// o'z tenantCode'ini oldindan biladi va foydalanuvchidan so'ramaydi").
//
// ⚠️ `MeResponse`da `currency` YO'Q — tenant valyutasi endi FAQAT
// `GET /api/selfcare/balance` javobidagi `money.currency` dan keladi
// (`AuthProvider.tsx` dagi izohga qarang).
//
// ⚠️ `dayTimezone` — tenantning IANA zonasi ("Asia/Kabul"). Backend kun
// chegarasini SHU zonada hisoblaydi, shuning uchun UI ham shu zonada
// ko'rsatishi kerak (`shared/lib/format.ts`).

import { z } from 'zod'
import { api } from '@/shared/api/client'
import { operationResultSchema, type OperationResult } from '@/shared/api/operationResult'
import { requireTenantCode } from '@/shared/config'

/** `uz.xsib.catalog.api.DeliveryMethod` */
export const DELIVERY_METHODS = ['PPPOE', 'HOTSPOT', 'STATIC'] as const
export type DeliveryMethod = (typeof DELIVERY_METHODS)[number] | 'UNKNOWN'

/** `uz.xsib.subscriber.api.SubscriberState` */
export const SUBSCRIBER_STATES = ['NEW', 'ACTIVE', 'SUSPENDED', 'BLOCKED', 'CLOSED'] as const
export type SubscriberState = (typeof SUBSCRIBER_STATES)[number] | 'UNKNOWN'

function isDeliveryMethod(value: string): value is (typeof DELIVERY_METHODS)[number] {
  return (DELIVERY_METHODS as readonly string[]).includes(value)
}

function isSubscriberState(value: string): value is (typeof SUBSCRIBER_STATES)[number] {
  return (SUBSCRIBER_STATES as readonly string[]).includes(value)
}

/**
 * Backend enum nomini xom matn sifatida qabul qiladi — noma'lum qiymat
 * kelsa ilova yiqilmasin. Boshqa fayllarda ham ishlatiladi (`balanceApi.ts`,
 * `plansApi.ts`) — bitta joyda, chunki `DeliveryMethod` bir nechta
 * endpoint javobida qaytadi.
 */
export function toDeliveryMethod(raw: string): DeliveryMethod {
  const upper = raw.trim().toUpperCase()
  return isDeliveryMethod(upper) ? upper : 'UNKNOWN'
}

function toSubscriberState(raw: string): SubscriberState {
  const upper = raw.trim().toUpperCase()
  return isSubscriberState(upper) ? upper : 'UNKNOWN'
}

const meSchema = z.object({
  login: z.string(),
  displayName: z.string().nullish(),
  kind: z.string(),
  state: z.string(),
  createdAt: z.string(),
  // `nullish` ATAYLAB: maydon backendga keyinroq qo'shilgan. Majburiy qilinsa,
  // eski versiyadagi server bilan `parse` yiqiladi va abonent umuman kira
  // olmaydi — vaqt zonasi noma'lum bo'lgani esa bundan ancha kichik muammo
  // (u holda brauzer zonasi ishlatiladi).
  dayTimezone: z.string().nullish(),
})

export type MeResponseRaw = z.infer<typeof meSchema>

export interface CurrentUser {
  login: string
  displayName: string
  kind: DeliveryMethod
  state: SubscriberState
  createdAt: string
  /** Tenantning IANA vaqt zonasi; server bermasa null */
  dayTimezone: string | null
}

function toCurrentUser(raw: MeResponseRaw): CurrentUser {
  return {
    login: raw.login,
    displayName: raw.displayName ?? raw.login,
    kind: toDeliveryMethod(raw.kind),
    state: toSubscriberState(raw.state),
    createdAt: raw.createdAt,
    dayTimezone: raw.dayTimezone ?? null,
  }
}

export interface LoginRequest {
  login: string
  password: string
}

const loginResponseSchema = z.object({
  token: z.string(),
  expiresAt: z.string().nullish(),
  subscriberId: z.string(),
  login: z.string(),
})

export interface LoginSuccess {
  token: string
  expiresAt: string | null
}

/** Kirish. ⚠️ TOTP YO'Q — abonent uchun ataylab (vazifa hujjati). */
export async function login(request: LoginRequest): Promise<LoginSuccess> {
  const tenantCode = requireTenantCode()
  const response = await api.post<unknown>('/api/selfcare/login', {
    tenantCode,
    login: request.login,
    password: request.password,
  })
  const parsed = loginResponseSchema.parse(response.data)
  return { token: parsed.token, expiresAt: parsed.expiresAt ?? null }
}

export async function fetchMe(): Promise<CurrentUser> {
  const response = await api.get<unknown>('/api/selfcare/me')
  return toCurrentUser(meSchema.parse(response.data))
}

export interface ChangePasswordRequest {
  currentPassword: string
  newPassword: string
}

export async function changePassword(request: ChangePasswordRequest): Promise<OperationResult> {
  const response = await api.put<unknown>('/api/selfcare/me/password', request)
  return operationResultSchema.parse(response.data)
}

/**
 * Chiqish — tokenni SERVER tomonda ham bekor qiladi.
 *
 * <h2>⚠️ Nega mahalliy tozalash yetmaydi</h2>
 * Ilgari bu funksiya bo'sh `Promise` qaytarardi va izohida "JWT holatsiz,
 * `/api/selfcare` da logout endpointi umuman yo'q" deb yozilgan edi. Bu
 * ma'lumot ESKIRGAN: `LogoutController` mavjud va u `jti` ni Redis qora
 * ro'yxatiga yozadi (`TokenBlacklist`), gateway va `SelfcareAuthFilter`
 * ikkalasi ham uni tekshiradi.
 *
 * Chaqirilmagani amaliy zarar berardi: internet-kafe yoki mehmonxona
 * lobbisidagi umumiy kompyuterda "Chiqish" bosilgandan keyin ham
 * o'g'irlangan token muddati (30 daqiqa) tugagunicha ISHLAYDI. Ya'ni
 * servis ataylab yopgan bo'shliq panelda qayta ochilib qolgan edi.
 *
 * <h2>⚠️ Xato YUTILADI</h2>
 * Tarmoq uzilgan yoki token allaqachon muddati o'tgan bo'lsa ham
 * foydalanuvchi CHIQISHI kerak. Endpoint idempotent, shuning uchun
 * xatoni ko'rsatish faqat zarar berardi: odam ekranda qolib, tokeni esa
 * mahalliy saqlanishda turaverardi.
 */
export async function logout(): Promise<void> {
  try {
    await api.post('/api/selfcare/logout')
  } catch {
    // ataylab jim — yuqoridagi izoh
  }
}
